import { cn } from '@anytag/shared-utils';
import { BaseModal, Button, Form } from '@anytag/shared-atoms';
import { useQueryHelper, useAllPackageCompanyOptions, useToggleState } from '@anytag/shared-hooks';
import {
  namedOperations,
  SocialAccountType,
  CompanyAndRolePair,
  useCreatePackageWithSocialAccountsMutation,
  useConnectPackageWithSocialAccountsMutation,
  useCreatePackageWithInfluencerAccountsMutation,
  useConnectPackageWithInfluencerAccountsMutation
} from '@anytag/graphql';
import { Template } from './Template';
import { AddPackageWithAccountsSchema } from './schema';
import { PackageWithAccountsFormValues } from './types';

interface AddPackageWithAccountsProps {
  className?: string;
  socialPairs: {
    influencerId: number;
    socialAccountId?: number | null;
    socialType?: SocialAccountType | null;
  }[];
  isInfluencerAccount?: boolean;
  influencerIds?: number[];
}

export const AddPackageWithAccounts = ({
  className,
  socialPairs,
  influencerIds = [],
  isInfluencerAccount
}: AddPackageWithAccountsProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { status: isOpenModal, toggleStatus: toggleModal } = useToggleState();
  const { allPackageCompanies, allPackageCompanyOptions } = useAllPackageCompanyOptions();

  const { callConnectPackageWithSocialAccounts } = useConnectPackageWithSocialAccountsMutation();
  const { callCreatePackageWithSocialAccounts } = useCreatePackageWithSocialAccountsMutation({
    refetchQueries: [namedOperations.Query.AllExistingPackages]
  });
  const { callConnectPackageWithInfluencerAccounts } = useConnectPackageWithInfluencerAccountsMutation();
  const { callCreatePackageWithInfluencerAccounts } = useCreatePackageWithInfluencerAccountsMutation({
    refetchQueries: [namedOperations.Query.AllExistingPackages]
  });

  const count = (isInfluencerAccount ? influencerIds.length : socialPairs.length) || 0;

  const handleSubmit = async (formValues: PackageWithAccountsFormValues) => {
    try {
      if (formValues.type === 'add') {
        const companyAndRolePairs =
          allPackageCompanies.reduce<CompanyAndRolePair[]>((rs, cr) => {
            if (formValues.companies?.includes(String(cr.id))) {
              rs.push({ companyId: cr.companyId, role: cr.role });
            }

            return rs;
          }, []) || [];

        if (formValues.name && formValues.countryId) {
          isInfluencerAccount
            ? await callCreatePackageWithInfluencerAccounts({
                variables: {
                  input: {
                    influencerIds,
                    companyAndRolePairs,
                    name: formValues.name,
                    countryId: formValues.countryId
                  }
                }
              })
            : await callCreatePackageWithSocialAccounts({
                variables: {
                  input: { name: formValues.name, countryId: formValues.countryId, socialPairs, companyAndRolePairs }
                }
              });
        }
      } else if (formValues.type === 'edit') {
        isInfluencerAccount
          ? await callConnectPackageWithInfluencerAccounts({
              variables: {
                input: {
                  influencerIds,
                  packageId: Number(formValues.id)
                }
              }
            })
          : await callConnectPackageWithSocialAccounts({
              variables: { input: { socialPairs, packageId: Number(formValues.id) } }
            });
      }

      enqueueSnackbar(t('package has been saved successfully'), { variant: 'success' });
      toggleModal();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={toggleModal}
        title={t('Button.package')}
        className={cn('min-w-27', className)}
        prefixIcon={{ icon: 'plus', size: '10px' }}
      />

      <BaseModal open={isOpenModal} onClose={toggleModal} asChild>
        <Form.Provider<PackageWithAccountsFormValues>
          onSubmit={handleSubmit}
          zodSchema={AddPackageWithAccountsSchema}
          defaultValues={{ id: '', type: 'edit' }}
        >
          <Template count={count} toggleModal={toggleModal} allPackageCompanyOptions={allPackageCompanyOptions} />
        </Form.Provider>
      </BaseModal>
    </>
  );
};
